import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { doc, onSnapshot, type FirestoreError } from "firebase/firestore";
import { auth, db } from "@/firebase";
import type { UserDoc, WithId } from "@/types/firestore";

const authListeners = new Set<() => void>();
let authUnsubscribe: (() => void) | null = null;
let snapshot: { status: AuthUserStatus; user: User | null } = {
  status: "loading",
  user: null,
};

type AuthSnapshot = typeof snapshot;

type AuthStoreSubscriber = () => void;

export type AuthUserStatus = "loading" | "unauthenticated" | "authed";

function notify() {
  for (const listener of authListeners) {
    listener();
  }
}

function ensureAuthSubscription() {
  if (authUnsubscribe) {
    return;
  }

  authUnsubscribe = onAuthStateChanged(
    auth,
    (firebaseUser) => {
      const timestamp = new Date().toISOString();
      console.log(`[T1-AUTH] onAuthStateChanged fired at ${timestamp}: user=${firebaseUser ? firebaseUser.uid : 'null'}`);
      
      snapshot = {
        status: firebaseUser ? "authed" : "unauthenticated",
        user: firebaseUser,
      };
      notify();
    },
    (error) => {
      console.error("[useAuthUser] Auth state subscription failed", error);
      snapshot = { status: "unauthenticated", user: null };
      notify();
    }
  );
}

function subscribeAuthStore(callback: AuthStoreSubscriber) {
  ensureAuthSubscription();
  authListeners.add(callback);
  return () => {
    authListeners.delete(callback);
    if (authListeners.size === 0 && authUnsubscribe) {
      authUnsubscribe();
      authUnsubscribe = null;
      snapshot = {
        status: "loading",
        user: null,
      };
    }
  };
}

function getAuthSnapshot(): AuthSnapshot {
  return snapshot;
}

function getServerSnapshot(): AuthSnapshot {
  return snapshot;
}

type UserDocState = {
  loading: boolean;
  userDoc: WithId<UserDoc> | null;
  error: FirestoreError | null;
};

const initialDocState: UserDocState = {
  loading: false,
  userDoc: null,
  error: null,
};

export type AuthUserState = {
  status: AuthUserStatus;
  firebaseUser: User | null;
  userDoc: WithId<UserDoc> | null;
  loading: boolean;
  error: FirestoreError | null;
};

export function useAuthUser(): AuthUserState {
  const authSnapshot = useSyncExternalStore(subscribeAuthStore, getAuthSnapshot, getServerSnapshot);
  const [docState, setDocState] = useState<UserDocState>(initialDocState);
  const uidRef = useRef<string | null>(null);

  const currentUser = authSnapshot.user;

  useEffect(() => {
    const nextUid = currentUser?.uid ?? null;
    uidRef.current = nextUid;

    if (!currentUser) {
      setDocState(initialDocState);
      return undefined;
    }

    setDocState((prev) => ({ ...prev, loading: true, error: null }));

    const userRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setDocState({ loading: false, userDoc: null, error: null });
          return;
        }

        const data = snap.data() as UserDoc;
        const nextDoc: WithId<UserDoc> = { id: snap.id, ...data };
        setDocState({ loading: false, userDoc: nextDoc, error: null });
      },
      (error) => {
        console.error("[useAuthUser] Failed to load user document", error);
        setDocState({ loading: false, userDoc: null, error });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  const combinedLoading = authSnapshot.status === "loading" || docState.loading;

  return useMemo(
    () => ({
      status: authSnapshot.status,
      firebaseUser: currentUser,
      userDoc: docState.userDoc,
      loading: combinedLoading,
      error: docState.error,
    }),
    [authSnapshot.status, currentUser, docState.error, docState.userDoc, combinedLoading]
  );
}
